import { computed, ComputedRef, reactive } from 'vue';
import { RouteRecordNormalized, useRoute } from 'vue-router';

import { Nullable } from '@/utils/types';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';

/**
 * Which of the bar's slots the page fills (set by AppTopBarContent). The title, the back
 * link and the greeting come from the route meta, so the bar reads those itself.
 */
export interface IAppTopBarContent {
  /** the page fills the inline / below-the-bar extension areas */
  hasExtension: boolean;
  hasActions: boolean;
  hasBelowExtension: boolean;
  extensionHeight: number;
}

/** Teleport targets the bar exposes to the page */
export const APP_TOP_BAR_ACTIONS_ID = 'app-top-bar-actions';
export const APP_TOP_BAR_EXTENSION_ID = 'app-top-bar-extension';
export const APP_TOP_BAR_EXTENSION_BELOW_ID = 'app-top-bar-extension-below';

const emptyContent = (): IAppTopBarContent => ({
  hasExtension: false,
  hasActions: false,
  hasBelowExtension: false,
  extensionHeight: 48,
});

// One bar per app, so one content; `owner` lets a page leaving only clear its own content
// even when the next page registered first.
const content = reactive<IAppTopBarContent & { owner: Nullable<symbol> }>({
  owner: null,
  ...emptyContent(),
});

export const useAppTopBarContent = () => content;

export const useAppTopBarRegistration = () => {
  const owner = Symbol('page');

  const setContent = (next: IAppTopBarContent): void => {
    Object.assign(content, next, { owner });
  };

  const clearContent = (): void => {
    if (content.owner !== owner) return;
    Object.assign(content, { owner: null }, emptyContent());
  };

  return { setContent, clearContent };
};

interface IUseAppShell {
  /** the app-shell branch AccountLayout renders, or null when the page draws its own v-main */
  appShellRecord: ComputedRef<Nullable<RouteRecordNormalized>>;
  /** AccountLayout draws AppTopBar (client users get the client header, which has no slots) */
  hasAppTopBar: ComputedRef<boolean>;
}

/** Read from the route, so AccountLayout and the page agree without passing anything down. */
export const useAppShell = (): IUseAppShell => {
  const route = useRoute();
  const { isClient } = useUserRoles();

  // matched[0] is AccountLayout; the next record is the branch it renders
  const appShellRecord = computed<Nullable<RouteRecordNormalized>>(() => {
    const record = route.matched[1];

    return record?.meta.appShell ? record : null;
  });
  const hasAppTopBar = computed<boolean>(() => !!appShellRecord.value && !isClient.value);

  return { appShellRecord, hasAppTopBar };
};
